import { usGetByQuestionId } from "@/services/assessments/options/get-by-question-id";
import { useFormLesson } from "@/stores/form-exam";
import { Add } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { InputDescription } from "./input-description";
import { InputImage } from "./input-image";
import { InputOption } from "./input-option";

export function SectionSelectedQuestion() {
  const [
    selectedQuestion,
    options,
    addOption,
    loading,
    setOption,
    clearOptionsForQuestion,
  ] = useFormLesson((state) => [
    state.selectedQuestion,
    state.options,
    state.addOption,
    state.loading,
    state.setOption,
    state.clearOptionsForQuestion,
  ]);

  // Ref para controlar se o componente está montado
  const isMountedRef = useRef(true);
  const currentQuestionIdRef = useRef<string | null>(null);

  // Carregar opções do backend quando uma questão é selecionada
  const {
    data: backendOptions,
    isLoading: isLoadingOptions,
    error: optionsError,
  } = usGetByQuestionId((selectedQuestion as any)?.id || "");

  // Cleanup quando componente desmonta - apenas para evitar memory leaks
  useEffect(() => {
    return () => {
      console.log("🔄 Component unmounting, setting isMountedRef to false");
      isMountedRef.current = false;
    };
  }, []);

  // Atualizar ref da questão atual
  useEffect(() => {
    if (selectedQuestion) {
      currentQuestionIdRef.current = (selectedQuestion as any)?.id;
      console.log(
        "Updated currentQuestionIdRef to:",
        currentQuestionIdRef.current
      );
    }
  }, [selectedQuestion]);

  // Carregar opções do backend para o store quando os dados chegarem
  const loadOptionsFromBackend = useCallback(
    (backendData: any[], questionId: string) => {
      // Verificar apenas se a questão ainda é a mesma
      // Não verificar isMountedRef aqui para permitir carregamento
      if (currentQuestionIdRef.current !== questionId) {
        return; // Evitar race conditions apenas para questões diferentes
      }

      try {
        // Limpar opções antigas da questão atual usando a função do store
        clearOptionsForQuestion(questionId);

        // Converter as opções do backend para o formato do store
        const formattedOptions = backendData.map((option) => ({
          id: option.id,
          questionId: option.questionId,
          description: option.description,
          isCorrect: option.isCorrect,
          position: option.position,
          isLocal: false, // Opções do backend não são locais
        }));

        // Atualizar o store com as opções do backend
        formattedOptions.forEach((option) => {
          // Verificar apenas se a questão ainda é a mesma
          if (currentQuestionIdRef.current === questionId) {
            setOption(option.id, option);
          } else {
          }
        });
      } catch (error) {
        console.error("Erro ao carregar opções:", error);
      }
    },
    [clearOptionsForQuestion, setOption]
  );

  // Effect para carregar opções do backend
  useEffect(() => {
    if (backendOptions?.data && selectedQuestion && !optionsError) {
      const questionId = (selectedQuestion as any)?.id;

      // Verificar se ainda é a questão atual
      if (currentQuestionIdRef.current === questionId) {
        loadOptionsFromBackend(backendOptions.data, questionId);

        // Log adicional para verificar se as opções foram carregadas
        setTimeout(() => {}, 100);
      } else {
      }
    } else {
    }
  }, [backendOptions, selectedQuestion, optionsError, loadOptionsFromBackend]);

  // Memoizar as opções da questão atual para evitar re-renders desnecessários
  const OPTIONS = useMemo(() => {
    if (!selectedQuestion) {
      return [];
    }

    const questionId = (selectedQuestion as any)?.id;
    const filteredOptions = (options || []).filter(
      ({ questionId: optionQuestionId }) => optionQuestionId === questionId
    );

    return filteredOptions;
  }, [options, selectedQuestion]);

  // Estados de loading e erro
  const isCurrentlyLoading = isLoadingOptions || loading;
  const hasError = !!optionsError;
  const hasOptions = OPTIONS.length > 0;
  const canAddMoreOptions = OPTIONS.length < 5;

  if (!selectedQuestion) {
    return null;
  }

  return (
    <section className="flex-1 flex-col flex py-3 px-5 gap-6 rounded-lg">
      <div className="flex flex-col gap-6">
        {/* Imagem e outros campos podem ser integrados ao TanStack Form depois */}
        <InputImage />
        <InputDescription />

        {/* Loading das opções */}
        {isCurrentlyLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-2">
              <CircularProgress size={32} />
              <span className="text-sm text-gray-500">
                Carregando opções...
              </span>
            </div>
          </div>
        )}

        {/* Erro ao carregar opções */}
        {hasError && !isCurrentlyLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm text-red-500">
                Erro ao carregar opções. Tente novamente.
              </span>
            </div>
          </div>
        )}

        {/* Lista de opções - cada uma gerencia seu próprio estado */}
        {!isCurrentlyLoading && !hasError && hasOptions && (
          <div className="space-y-4">
            {OPTIONS.map((option, index) => (
              <InputOption
                key={option.id}
                option={option}
                index={index + 1}
                enableDeleteOption={OPTIONS.length > 1}
              />
            ))}
          </div>
        )}

        {/* Mensagem quando não há opções */}
        {!isCurrentlyLoading && !hasError && !hasOptions && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm text-gray-500">
                Nenhuma opção encontrada para esta questão
              </span>
            </div>
          </div>
        )}

        {/* Botão para adicionar nova opção */}
        {!isCurrentlyLoading && !hasError && canAddMoreOptions && (
          <Button
            onClick={() => addOption((selectedQuestion as any).id)}
            variant="outlined"
            color="secondary"
            disabled={isCurrentlyLoading}
            className="w-full !px-4 !bg-transparent !border-dashed !text-[#858585] hover:!text-primary hover:!border-primary"
          >
            <Add />
            <span>new option</span>
          </Button>
        )}
      </div>
    </section>
  );
}
